import {
    useInfiniteQuery,
    useMutation,
    useQuery,
    useQueryClient,
} from '@tanstack/react-query';

import {I18n} from '@aws-amplify/core';
import * as ProductsService from '../services/ProductsService';
import {
    notifyError,
    notifySuccess,
    showErrorNotifications,
} from '../../../components/ServiceNotifications';

export const PAGE_SIZE = 50;

// Structured keys: ['products', 'list', {...}] and ['products', 'detail', id].
// An update invalidates the detail entry and every list in one call each.
export const productsKeys = {
    all: ['products'] as const,
    lists: () => [...productsKeys.all, 'list'] as const,
    list: () => [...productsKeys.lists(), {pageSize: PAGE_SIZE}] as const,
    details: () => [...productsKeys.all, 'detail'] as const,
    detail: (id: string) => [...productsKeys.details(), id] as const,
};

const STALE_TIME = 30_000;

export const useProductsInfiniteQuery = () => {
    return useInfiniteQuery({
        queryKey: productsKeys.list(),
        queryFn: ({pageParam}: { pageParam: string | null }) =>
            ProductsService.fetchRecords(PAGE_SIZE, pageParam),
        getNextPageParam: (lastPage) => lastPage?.nextToken ?? undefined,
        initialPageParam: null as string | null,
        // Set deliberately: the default of 0 refetches every loaded page on
        // each mount, so paging down and opening a product would pay for the
        // whole window again on the way back.
        staleTime: STALE_TIME,
    });
};

export const useProductQuery = (id: string) => {
    return useQuery({
        queryKey: productsKeys.detail(id),
        queryFn: () => ProductsService.fetch(id),
        enabled: !!id,
        staleTime: STALE_TIME,
    });
};

// An AppSync refusal arrives as `{errors: [{errorType, message}]}`, not as an
// Error. The API's own message is the useful one ("This product was changed
// since you opened it. Reload it and make your edit again."), so it is shown
// as it is; anything else falls back to the generic notification.
const reportError = (e: unknown) => {
    const errors = (e as { errors?: unknown } | null)?.errors;
    if (Array.isArray(errors) && errors.length > 0) {
        showErrorNotifications(e);
        return;
    }
    notifyError(e);
};

export const useCreateProductMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ProductsService.create,
        onSuccess: () => {
            notifySuccess(I18n.get('Product saved as a draft'));
            return queryClient.invalidateQueries({queryKey: productsKeys.lists()});
        },
        onError: reportError,
    });
};

export const useUpdateProductMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ProductsService.update,
        onSuccess: (_, input) => {
            notifySuccess(I18n.get('Product updated'));
            return Promise.all([
                queryClient.invalidateQueries({queryKey: productsKeys.detail(input.id)}),
                queryClient.invalidateQueries({queryKey: productsKeys.lists()}),
            ]);
        },
        onError: reportError,
    });
};
